import branchAxios from '../axios';
import { GET_NOTIFICATION_TOKENS, SEND_PUSH_NOTIFICATION, SEND_NOTIFICATION } from '../ApiUrl';

function parseNotificationTokensResponse(res) {
  if (res.hasError) {
    const message =
      typeof res.message === 'string' && res.message.trim()
        ? res.message.trim()
        : 'Could not load notification tokens.';
    return { ok: false, error: message, data: [], pagination: null };
  }

  const body = res.data;
  if (body?.success === false) {
    return {
      ok: false,
      error:
        typeof body.message === 'string' && body.message.trim()
          ? body.message.trim()
          : 'Request failed.',
      data: [],
      pagination: null,
    };
  }

  if (body?.success === true && Array.isArray(body.data)) {
    return {
      ok: true,
      error: null,
      data: body.data,
      pagination: body.pagination ?? null,
    };
  }

  return { ok: false, error: 'Unexpected response from notification tokens API.', data: [], pagination: null };
}

function parseSendPushResponse(res) {
  if (res.hasError) {
    return {
      ok: false,
      message:
        typeof res.message === 'string' && res.message.trim()
          ? res.message.trim()
          : 'Failed to send notification.',
    };
  }

  const body = res.data;
  if (body?.success === true) {
    const devices = body.data?.devices_notified;
    const base = body.message || 'Notification sent successfully.';
    return {
      ok: true,
      message: devices != null ? `${base} (${devices} device${devices === 1 ? '' : 's'})` : base,
      data: body.data ?? null,
    };
  }

  const errors = Array.isArray(body?.errors) ? body.errors : [];
  const errorMsg = errors
    .map((e) => e?.ErrorMessage || e?.message)
    .filter(Boolean)
    .join('; ');

  return {
    ok: false,
    message:
      errorMsg ||
      (typeof body?.message === 'string' && body.message.trim()
        ? body.message.trim()
        : 'Failed to send notification.'),
  };
}

/**
 * Build recipient object for POST /SendNotification from a token list row.
 * @param {{ account_type?: string, email?: string, user_id?: string, kitchen_name?: string }} user
 */
export function buildNotificationRecipient(user) {
  const account_type = String(user?.account_type || '').toLowerCase();
  if (!['kitchen', 'driver', 'customer'].includes(account_type)) {
    return { ok: false, error: 'Invalid or missing account type for this user.' };
  }

  const recipient = { account_type };

  if (account_type === 'kitchen') {
    const kitchen_name = String(user?.kitchen_name || '').trim().toLowerCase();
    if (!kitchen_name) {
      return { ok: false, error: 'Kitchen name is required for kitchen notifications.' };
    }
    recipient.kitchen_name = kitchen_name;
    return { ok: true, recipient };
  }

  if (user?.email) {
    recipient.email = String(user.email).trim().slice(0, 50);
    return { ok: true, recipient };
  }

  if (user?.user_id) {
    recipient.user_id = String(user.user_id);
    return { ok: true, recipient };
  }

  return { ok: false, error: 'Email or user ID is required for this recipient.' };
}

/**
 * GET /GetNotificationTokens
 * @param {{ account_type?: string, search?: string, page?: number, limit?: number }} [params]
 */
export async function getNotificationTokensAPI(params = {}) {
  try {
    const qs = new URLSearchParams();
    if (params.account_type) qs.set('account_type', String(params.account_type));
    if (params.search) qs.set('search', String(params.search));
    if (params.page != null) qs.set('page', String(params.page));
    if (params.limit != null) qs.set('limit', String(params.limit));
    const query = qs.toString();
    const url = query ? `${GET_NOTIFICATION_TOKENS}?${query}` : GET_NOTIFICATION_TOKENS;
    const response = await branchAxios.get(url);
    return response;
  } catch (error) {
    console.log('getNotificationTokensAPI catch', error);
    return { hasError: true, error };
  }
}

export async function loadNotificationTokens(params = {}) {
  const res = await getNotificationTokensAPI(params);
  const parsed = parseNotificationTokensResponse(res);
  return parsed;
}

/**
 * POST /SendPushNotification
 * @param {{ token?: string, device_id?: string, title: string, subtitle: string, channel_id?: string }} payload
 */
export async function sendPushNotificationAPI(payload) {
  try {
    const response = await branchAxios.post(SEND_PUSH_NOTIFICATION, payload);
    return response;
  } catch (error) {
    console.log('sendPushNotificationAPI catch', error);
    return { hasError: true, error };
  }
}

export async function sendPushNotification(payload) {
  const res = await sendPushNotificationAPI(payload);
  return parseSendPushResponse(res);
}

/**
 * POST /SendNotification
 * @param {{ recipient: object, notification: { type: string, title: string, description: string, image?: string, extra_data?: object } }} payload
 */
export async function sendNotificationAPI(payload) {
  try {
    const response = await branchAxios.post(SEND_NOTIFICATION, payload);
    return response;
  } catch (error) {
    console.log('sendNotificationAPI catch', error);
    return { hasError: true, error };
  }
}

export async function sendNotification({ recipient, notification }) {
  const res = await sendNotificationAPI({ recipient, notification });
  return parseSendPushResponse(res);
}
